import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  User,
  Calendar,
  FileText,
  Building2,
  Filter,
  Search,
  MoreVertical,
  X,
  CheckCheck,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { mockClientsService as clientsService } from '@/services/api/mock-clients-data';
import { cn } from '@/lib/utils';
import type { ActionItem, ActionPriority, ActionCategory, ActionStatus } from '@/types/client';

const priorityConfig: Record<ActionPriority, { label: string; className: string; icon: React.ReactNode }> = {
  urgent: { label: 'Urgent', className: 'bg-red-500/10 text-red-500 border-red-500/20', icon: <AlertTriangle className="h-3 w-3" /> },
  high: { label: 'High', className: 'bg-orange-500/10 text-orange-500 border-orange-500/20', icon: <AlertTriangle className="h-3 w-3" /> },
  medium: { label: 'Medium', className: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20', icon: <Clock className="h-3 w-3" /> },
  low: { label: 'Low', className: 'bg-gray-500/10 text-gray-500 border-gray-500/20', icon: <Clock className="h-3 w-3" /> },
};

const categoryLabels: Record<ActionCategory, string> = {
  id_renewal: 'ID Renewal',
  document_expiry: 'Document Expiry',
  compliance: 'Compliance',
  lodgement: 'Lodgement',
  review: 'Review Required',
  follow_up: 'Follow Up',
};

export function ActionListsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState<ActionPriority | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<ActionCategory | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ActionStatus>('pending');
  const [selectedActions, setSelectedActions] = useState<Set<string>>(new Set());

  // Fetch actions
  const { data: actions = [], isLoading } = useQuery({
    queryKey: ['action-items', priorityFilter, categoryFilter, statusFilter],
    queryFn: () => clientsService.getActionItems({ priority: priorityFilter === 'all' ? undefined : priorityFilter, category: categoryFilter === 'all' ? undefined : categoryFilter, status: statusFilter }),
  });

  // Update action status mutation
  const updateActionMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: ActionStatus }) =>
      clientsService.updateActionStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['action-items'] });
      toast.success('Action updated');
    },
  });

  const filteredActions = actions.filter((action) =>
    action.title.toLowerCase().includes(search.toLowerCase()) ||
    action.clientName.toLowerCase().includes(search.toLowerCase()) ||
    action.description.toLowerCase().includes(search.toLowerCase())
  );

  const groupedActions = filteredActions.reduce((acc, action) => {
    if (!acc[action.priority]) {
      acc[action.priority] = [];
    }
    acc[action.priority].push(action);
    return acc;
  }, {} as Record<ActionPriority, ActionItem[]>);

  const handleSelectAction = (id: string) => {
    const newSelected = new Set(selectedActions);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedActions(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedActions.size === filteredActions.length) {
      setSelectedActions(new Set());
    } else {
      setSelectedActions(new Set(filteredActions.map(a => a.id)));
    }
  };

  const handleBulkComplete = () => {
    if (selectedActions.size === 0) return;

    Array.from(selectedActions).forEach(id => {
      updateActionMutation.mutate({ id, status: 'completed' });
    });

    setSelectedActions(new Set());
    toast.success(`Marked ${selectedActions.size} action(s) as complete`);
  };

  const handleNavigateToClient = (clientId: string) => {
    navigate(`/panel/files/clients/${clientId}`);
  };

  const getPriorityOrder = (priority: ActionPriority): number => {
    const order = { urgent: 0, high: 1, medium: 2, low: 3 };
    return order[priority];
  };

  const sortedPriorities = Object.keys(groupedActions).sort((a, b) =>
    getPriorityOrder(a as ActionPriority) - getPriorityOrder(b as ActionPriority)
  ) as ActionPriority[];

  const stats = {
    total: filteredActions.length,
    urgent: filteredActions.filter(a => a.priority === 'urgent').length,
    dueToday: filteredActions.filter(a => {
      if (!a.dueDate) return false;
      const today = new Date().toISOString().split('T')[0];
      return a.dueDate.split('T')[0] === today;
    }).length,
    estimatedTime: filteredActions.reduce((sum, a) => sum + (a.estimatedTime || 0), 0),
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">AI-Generated Action Lists</h1>
        <p className="text-muted-foreground mt-2">
          Automated weekly action items generated from client file analysis
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Actions</p>
                <p className="text-2xl font-bold mt-1">{stats.total}</p>
              </div>
              <CheckCheck className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Urgent</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{stats.urgent}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Due Today</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">{stats.dueToday}</p>
              </div>
              <Calendar className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Est. Time</p>
                <p className="text-2xl font-bold mt-1">{Math.round(stats.estimatedTime / 60)}h</p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search actions or clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Select value={priorityFilter} onValueChange={(v) => setPriorityFilter(v as ActionPriority | 'all')}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Priorities</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>

              <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as ActionCategory | 'all')}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(categoryLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as ActionStatus)}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {selectedActions.size > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                {selectedActions.size} action(s) selected
              </p>
              <Button size="sm" onClick={handleBulkComplete}>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Mark Complete
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Lists */}
      {isLoading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Loading actions...</p>
          </CardContent>
        </Card>
      ) : filteredActions.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No actions found</h3>
            <p className="text-sm text-muted-foreground">
              {statusFilter === 'completed' ? 'No completed actions yet' : 'All caught up!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {sortedPriorities.map((priority) => {
            const items = groupedActions[priority];
            if (!items || items.length === 0) return null;

            return (
              <Card key={priority}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge className={priorityConfig[priority].className}>
                        {priorityConfig[priority].icon}
                        <span className="ml-1">{priorityConfig[priority].label}</span>
                      </Badge>
                      <CardTitle className="text-lg">{items.length} action{items.length !== 1 ? 's' : ''}</CardTitle>
                    </div>
                    <Checkbox
                      checked={items.every(item => selectedActions.has(item.id))}
                      onCheckedChange={(checked) => {
                        const newSelected = new Set(selectedActions);
                        items.forEach(item => {
                          if (checked) {
                            newSelected.add(item.id);
                          } else {
                            newSelected.delete(item.id);
                          }
                        });
                        setSelectedActions(newSelected);
                      }}
                    />
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {items.map((action) => (
                      <div
                        key={action.id}
                        className="p-4 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedActions.has(action.id)}
                            onCheckedChange={() => handleSelectAction(action.id)}
                            className="mt-1"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1">
                                <h3 className="font-medium text-sm mb-1">{action.title}</h3>
                                <p className="text-sm text-muted-foreground mb-2">
                                  {action.description}
                                </p>
                                <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                                  <button
                                    onClick={() => handleNavigateToClient(action.clientId)}
                                    className="flex items-center gap-1 hover:text-foreground transition-colors"
                                  >
                                    <Building2 className="h-3 w-3" />
                                    {action.clientName}
                                  </button>
                                  <span className="flex items-center gap-1">
                                    <Badge variant="outline" className="text-[10px] h-4">
                                      {categoryLabels[action.category]}
                                    </Badge>
                                  </span>
                                  {action.dueDate && (
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {new Date(action.dueDate).toLocaleDateString('en-AU')}
                                    </span>
                                  )}
                                  {action.estimatedTime && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {action.estimatedTime} min
                                    </span>
                                  )}
                                  {action.assignedTo && (
                                    <span className="flex items-center gap-1">
                                      <User className="h-3 w-3" />
                                      {action.assignedTo}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => updateActionMutation.mutate({ id: action.id, status: 'in_progress' })}>
                                    Start Working
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => updateActionMutation.mutate({ id: action.id, status: 'completed' })}>
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Mark Complete
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => handleNavigateToClient(action.clientId)}>
                                    <ArrowRight className="h-4 w-4 mr-2" />
                                    View Client
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onClick={() => updateActionMutation.mutate({ id: action.id, status: 'dismissed' })} className="text-red-600">
                                    Dismiss
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
